/**
 * Imports - External
 * --------------------------------------------------------------------------
 */

import { faDna, faEmptySet, faFile, faFilePdf, faLock, faLockOpen, faRepeat, faUser } from '@fortawesome/duotone-light-svg-icons';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DoneIcon from '@mui/icons-material/Done';
import DownloadIcon from '@mui/icons-material/Download';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormatPaintIcon from '@mui/icons-material/FormatPaint';
import GradingIcon from '@mui/icons-material/Grading';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Accordion, AccordionDetails, AccordionSummary, AlertTitle, Box, BoxProps, Button, Divider, Grid, IconButton, Link, List, ListItem, Paper, Radio, RadioGroup, RadioProps, Tooltip, Typography, darken, lighten, styled, useTheme } from "@mui/material";
import { grey } from '@mui/material/colors';
import { WindowManager, WindowReceivedEvent } from '@ngt/redact';
import moment from 'moment';
import { FC, Fragment, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

/**
 * Imports - Internal
 * --------------------------------------------------------------------------
 */

import { DocumentEvent, DocumentFile, DocumentForm, GetOpenClinicaDocument, Institution, ItemDataType, ParticipantDocument, SendNotifySitesOfProcessedAndVerifiedDocumentsEmail, StudySubjectStatus, UpdateOpenClinicaDocument } from '@/apis/dtos';
import AppSettings from '@/AppSettings';
import RedactSelectDialog from '@/components/dialogs/RedactSelectDialog';
import Loading from '@/components/layout/Loading';
import Show from '@/components/layout/Show';
import AlertContext from '@/contexts/data/AlertContext';
import { DocumentVerificationEventContext } from '@/contexts/data/DocumentVerificationEventContext';
import ParticipantContext from '@/contexts/data/ParticipantContext';
import ParticipantDocumentContext from '@/contexts/data/ParticipantDocumentContext';
import { ParticipantDocumentStateContext } from '@/contexts/data/ParticipantDocumentStateContext';
import { TrialContext } from '@/contexts/data/TrialContext';
import { useBreadcrumbs } from '@/contexts/layout/BreadcrumbContext';
import useServiceStackClient from '@/hooks/apis/useServiceStackClient';
import type { IWindowManagerMessageData } from '@ngt/redact/src/WindowManager';
import useGetTrialInstitutionCoordinators from '@/hooks/data/useGetTrialInstitutionCoordinators';
import useGetTrialInstitutions from '@/hooks/data/useGetTrialInstitutions';
import useGetTrialCoordinatingGroups from '@/hooks/data/useGetTrialCoordinatingGroups';
import { useSessionStorage } from 'react-use/lib';
import { faBell } from '@fortawesome/pro-solid-svg-icons';


/**
 * Interfaces
 * --------------------------------------------------------------------------
 */

interface IParticipantFileContainerProps {
    event?: DocumentEvent;
    form?: DocumentForm;
    details?: DocumentFile[];
}

/**
 * Functions
 * --------------------------------------------------------------------------
 */

const getItemDataValue = (value: string, optionsText: string, optionsValues: string) => {
    let regex: RegExp = /(?<!\\),/;
    const ids: string[] = optionsValues.split(regex);
    const labels: string[] = optionsText.split(regex);

    const idx = ids.indexOf(value);

    if (!idx) {
        return undefined;
    }
    
    if (idx >= labels.length) {
        return undefined;
    }

    return labels[idx];
}

/**
 * Components
 * --------------------------------------------------------------------------
 */

const AccordionStyled = styled(Accordion)(({ theme }) => ({
    backgroundColor: theme.palette.primary.main,
    marginBottom: '15px'
}));

const AccordionDetailsStyled = styled(AccordionDetails)(() => ({
    backgroundColor: 'white',
    padding: 0
}));

const ExpandMoreIconStyled = styled(ExpandMoreIcon)(() => ({
    color: 'white',
    fontSize: '2.0rem'
}));

const VerticalCenter = styled(Box, { shouldForwardProp: (props) => props !== 'toRight' && props !== 'toCenter' })<BoxProps & { toRight?: boolean, toCenter?: boolean }>(({ toRight, toCenter }) => ({
    height: '100%',
    display: 'flex',
    justifyContent: toCenter ? 'center' : toRight ? 'right' : 'left',
    alignItems: 'center',
}));

const DocumentIconContainer = styled(Box)(({ theme }) => ({
    display: 'inline-block',
    backgroundColor: theme.palette.primary.main,
    height: '60px',
    width: '60px'
}));

const DocumentIconDetails = styled(Box)(() => ({
    display: 'inline-block',
    height: '60px',
    minWidth: '160px',
    paddingRight: '5px'
}));

const StyledRadio = styled(Radio, { shouldForwardProp: (props) => props !== 'staticColor'})<RadioProps & { staticColor?: 'info' | 'success' | 'error' | 'warning' }>(({ theme, staticColor, disabled }) => ({
    minWidth: 0,
    margin: '0 3px',
    borderRadius: '5px',
    px: 1.5,
    alignItems: 'center',
    transition: '0.2s',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'light' ? '#ecdcf5': darken('#ecdcf5', 0.3),

    },
    '&.Mui-checked': {
        backgroundColor: disabled ? grey[400] :
            staticColor ? theme.palette[staticColor].main : theme.palette.success.main,
        transition: '0.2s',
        color: 'white'
    },
}));

/**
 * Components - Main
 * --------------------------------------------------------------------------
 */

const ParticipantFileContainer: FC<IParticipantFileContainerProps> = ({
    event,
    form,
    details
}) => {
    const client = useServiceStackClient();
    //const theme = useTheme();
    const { participantCode } = useParams();

    const { addAlert } = useContext(AlertContext);
    const { data: documentStates, updateDocumentState } = useContext(ParticipantDocumentStateContext);
    const { refresh: refreshFiles } = useContext(DocumentVerificationEventContext);
    const { refresh } = useContext(ParticipantContext);
    const { data: trial } = useContext(TrialContext);


    const processedAndVerifiedDocuments = useMemo(() => {
        return documentStates?.filter(doc => doc.documentState === 2) ?? [];
    }, [documentStates]);

    const [redactSelectDialogOpen, setRedactSelectDialogOpen] = useState(false);

    // Document verification file data
    const file = useMemo(() => {
        const file: { type?: DocumentFile, name?: DocumentFile, document?: DocumentFile } = {
            type: undefined,
            name: undefined,
            document: undefined
        }

        if (details) {
            file.type = details.filter((x) => x.label === 'Type')?.[0];
            file.name = details.filter((x) => x.label === 'Name')?.[0];
            file.document = details.filter((x) => x.itemDataType === ItemDataType.File)?.[0];
        }

        return file;
        
    }, [details]);

    // Document verification display details
    const display = useMemo(() => {
        const file: { type?: string, name?: string, document?: string } = {
            type: undefined,
            name: undefined,
            document: undefined
        }

        if (details) {
            if(details.length > 1) {
                const type = details.filter((x) => x.label === 'Type')?.[0];
                const name = details.filter((x) => x.label === 'Name')?.[0];
                const document = details.filter((x) => x.itemDataType === ItemDataType.File)?.[0];

                // -- Document type
                if (!type?.value) {
                    file.type = 'unknown'
                } else {
                    file.type = getItemDataValue(type.value, type?.optionsText ?? '', type?.optionsValues ?? '') ?? 'unknown';
                }

                // -- Document name
                file.name = name?.value ?? 'unknown'

                // -- Document path
                file.document = document?.value ?? 'unknown'
            } else {
                const document = details.filter((x) => x.itemDataType === ItemDataType.File)?.[0];

                file.type = document?.label ?? 'unknown';

                file.name = 'unknown';

                file.document = document?.value ?? 'unknown'
            }
        }

        return file;

    }, [details]);

    // Document state
    const [document, setDocument] = useState<ParticipantDocument>(() => {
        let pd;
        if (documentStates && documentStates.length > 0) {
            pd = documentStates.filter(x => x.documentId === file.document?.itemDataId)[0];
        } 

        if (!pd) {
            pd = new ParticipantDocument();
            pd.documentId = file.document?.itemDataId;
            pd.documentState = 0;
            pd.participantLabel = participantCode;
        }

        return pd;
    });

    // State of the action buttons
    const [documentState, setDocumentState] = useState<string | null>(null);
    
    // Redact Window manager
    const [instance, setInstance] = useState<WindowManager | undefined>(undefined);
    const [updating, setUpdating] = useState<boolean>(false);


    useEffect(() => {
        if (documentStates) { 

            var d = documentStates
        .filter(ds => ds.documentId === file.document?.itemDataId)
        .sort((a, b) => new Date(b.dateEntered ?? 0).getTime() - new Date(a.dateEntered ?? 0).getTime())[0];
            if (d) {
                setDocument(d);
                setDocumentState(d.documentState?.toString() ?? "0");
            } else {
                setDocumentState("0");
            }
        }

    }, [documentStates, setDocument, setDocumentState])

    // Determine if the file can be redacted (Currently only pdf. I am still yet to see if word docs work)
    const isInvalidFileType = useMemo(() => {
        if (file?.document) {
            return display?.document?.slice(display.document.lastIndexOf("."), display.document.length) !== '.pdf';
        }

        return true;

    }, [file, file?.document])

    // Change the redacted state of the document
    const handleStateClick = useCallback(async (value: string) => {
        try {
            setUpdating(true);

            var state = parseInt(value);

            if (isNaN(state)) {
                state = 0;
            }

            if (state == 2) {
                setRedactSelectDialogOpen(true);
                return;
            }

            // Get the timestamp from the files name
            var reg = /(?<OcFileName>\\_(?<OriginalFileName>.*?)(?<Timestamp>\d{14}\+\d{4})(?<Extension>\..*?)?$)/

            const match = reg.exec(file?.document?.value ?? '');

            var fileTimestamp = moment(match?.groups?.['Timestamp'] ?? new Date(), 'YYYYMMDDHHmmssZZ').toDate();

            // Append to request object
            let copy = document;
            copy.redacted = fileTimestamp ? match?.groups?.['Timestamp'] : moment(new Date()).format('YYYYMMDDHHmmssZZ');
            copy.documentState = state;            

            // Update the redacted state of the document
            const updateResponse = updateDocumentState ? await updateDocumentState(copy) : undefined;

            if (
                !updateResponse || 
                updateResponse.responseStatus?.errorCode
            ) {
                throw new Error(updateResponse?.responseStatus?.errorCode ?? "Missing update response status")
            }

            setDocument(c => ({ ...c, documentState: state }));

            setDocumentState(value);

        } 
        catch (error) {
            // Do nothing...
            //why are we atching an error we're doing nothing with?
        } 
        finally {
            setUpdating(false);
        }

    }, [document, updateDocumentState, setDocument, setDocumentState]);

    const handlePostDialogSave = useCallback(async (redactedFields?: string[], comments?: string) => {
        try {
            var reg = /(?<OcFileName>\\_(?<OriginalFileName>.*?)(?<Timestamp>\d{14}\+\d{4})(?<Extension>\..*?)?$)/
    
            const match = reg.exec(file?.document?.value ?? '');
    
            var date = moment(match?.groups?.['Timestamp'] ?? new Date(), 'YYYYMMDDHHmmssZZ').toDate();
    
            // Append to request object
            let copy = document;
            copy.documentState = 2;
            copy.redacted = date ? match?.groups?.['Timestamp'] : moment(new Date()).format('YYYYMMDDHHmmssZZ');
            
            // Update the redacted state of the document
            const updateResponse = updateDocumentState ? await updateDocumentState(copy, redactedFields, comments) : undefined;
    
            if (
                !updateResponse || 
                updateResponse.responseStatus?.errorCode
            ) {
                throw new Error(updateResponse?.responseStatus?.errorCode ?? "Missing update response status")
            }
    
            setDocument(c => ({ ...c, documentState: 2 }));
    
            setDocumentState("2");
        } 
        catch(error) {
            //why are we atching an error we're doing nothing with?
        }
        finally {

        }
    }, [document, updateDocumentState, setDocument, setDocumentState]);

    const getDocumentFile: () => Promise<File> = useCallback(async () => {
        var request = new GetOpenClinicaDocument();
        request.fileName = display?.document;

        var blob = await client.get(request);

        return new File([blob], display?.document ?? '');
            
    }, [file, file?.document, display]);

    const handleRedactClick = useCallback(() => {
        // Clear old instance if exists
        if (instance) {
            instance.dispose();
        }

        const percentageWidth = 0.5;
        const percentageHeight = 0.9;

        // Fixes dual-screen position                             Most browsers      Firefox
        const dualScreenLeft = window.screenLeft !==  undefined ? window.screenLeft : window.screenX;
        const dualScreenTop = window.screenTop !==  undefined   ? window.screenTop  : window.screenY;

        const screenWidth = window.innerWidth ? window.innerWidth : screen.width;
        const screenHeight = window.innerHeight ? window.innerHeight : screen.height;

        const width = screenWidth * percentageWidth;
        const height = screenHeight * percentageHeight;

        const systemZoom = screenWidth / window.screen.availWidth;
        const left = (screenWidth - width) / 2 / systemZoom + dualScreenLeft
        const top = (screenHeight - height) / 2 / systemZoom + dualScreenTop

        ///////////////////////////////////////////////////////////////////////////////////////////
        // Note: Getting the OC details
        //
        // We need to perform regex check to get the file path, the occurrence of 'redacted',
        // the timestamp and the extension.
        //
        // For OC, we need to strip the timestamp (otherwise this will be appended when pushed to OC).
        // For the redaction tool, we need to strip any occurrences of '-redacted' (otherwise this will
        // be appended also).
        //
        // Declare the "OC format" as a file name with the date and time at the end of the file. The format
        // of this date and time is:
        //  - 14 characters for the date and time
        //  - 1 character (+ or _)
        //  - 4 characters for the offset
        ///////////////////////////////////////////////////////////////////////////////////////////
        const regex = /^(?<FilePath>.*).*(?<Timestamp>(?:\d{14}[\+_]\d{4}).?)(?<Extension>\..*?)$/
        const match = regex.exec(file.document?.value ?? 'unknown');

        let newFileName = file.document?.value ?? 'unknown';

        // If there was a match detected in the regex. Otherwise, just return the existing file path, lol
        if (match !== null) {
            let groups = match?.groups;

            if (groups) {
                // Update the file path / name (remove the redact)
                let redactedIndex = groups['FilePath'].lastIndexOf('redacted');
                let newFilePath = groups['FilePath'];

                if (redactedIndex >= 0) {
                    // strip the -redacted off the end of the file path value
                    newFilePath = groups['FilePath'].substring(0, (redactedIndex - 1))
                }

                // Replace the time stamp with '-redacted' instead
                newFileName = `${newFilePath}-redacted${groups['Extension']}`;
            }
        }

        const manager = new WindowManager(
            AppSettings.redactUrl,
            {
                newFileName: newFileName,
                hostOrigin: window.location.origin,
                instanceId: uuidv4(),
                windowName: 'redaction_window',
                windowOptions: {
                    width: (width / systemZoom).toString(),
                    height: (height / systemZoom).toString(),
                    top: top.toString(),
                    left: left.toString()
                },
                onInit: async () => {
                    try {
                        var fileObj = await getDocumentFile();

                        if (!fileObj || !(fileObj instanceof File)) {
                            throw new Error('Failed to retrieve file')
                        }

                        manager.postMessage<File>(fileObj, WindowReceivedEvent.Update);
                    } 
                    catch(error: any) {
                        var msg = error.responseStatus ? error.responseStatus.message : error.message;
                        
                        manager.postMessage<string>(msg, WindowReceivedEvent.Error);

                        addAlert({
                            content: <AlertTitle>
                                    {msg}
                                </AlertTitle>,
                            timeout: 5000,
                            severity: 'error',
                            dismissable: true,
                        });
                    }
                },
                onUpdate: async (data: IWindowManagerMessageData<any> | undefined) => {
                    try {
                        setUpdating(true);

                        // Request object
                        var request = new UpdateOpenClinicaDocument();
                        request.documentId = file?.document?.itemDataId;

                        request.studyOid = trial?.oid;
                        request.studySubjectLabel = participantCode;
                        request.eventOid = event?.oid;
                        request.eventRepeat = event?.repeat;
                        request.formOid = form?.formOid;
                        request.itemName = file?.document?.name;
                        request.itemRepeat = file?.document?.ordinal;
        
                        // File object
                        var formData = new FormData();
                        formData.append("file", data?.payload);
        
                        var res = await client.apiForm(request, formData);

                        if (res.error) {
                            throw res.error;
                        }

                        await refresh();

                        await refreshFiles();
        
                        manager.postMessage<string>("", WindowReceivedEvent.Finish);

                        addAlert({
                            content: <AlertTitle>
                                    Document updated successfully.
                                </AlertTitle>,
                            timeout: 5000,
                            severity: 'success',
                            dismissable: true,
                        });
                    } 
                    catch (error: any) {
                        var msg = error.responseStatus ? error.responseStatus.message : error.message;
                        
                        manager.postMessage<string>(msg, WindowReceivedEvent.Error);

                        addAlert({
                            content: <AlertTitle>
                                    {msg}
                                </AlertTitle>,
                            timeout: 5000,
                            severity: 'error',
                            dismissable: true,
                        });
                    } 
                    finally {
                        setUpdating(false);
                    }
                },
                onFinish: async () => {
                    manager.dispose();
                },
                onError: async (message: string | undefined) => {
                    addAlert({
                        content: <AlertTitle>
                                {message}
                            </AlertTitle>,
                        timeout: 5000,
                        severity: 'error',
                        dismissable: true,
                    });

                    manager.dispose();
                }
            }
        );

        manager.open();

        setInstance(manager);

    }, [getDocumentFile, client, addAlert, setUpdating, refresh, refreshFiles, setInstance, file, instance, display]);

 
    const theme = useTheme();

    useEffect(() => {
        const close = () => {
            instance?.dispose();
            setInstance(undefined);
        };

        window.addEventListener("beforeunload", close);

        return () => {
            window.removeEventListener("beforeunload", close)
        }
    }, [instance, setInstance]);
    


    return (
        <ParticipantDocumentContext.Provider value={document}>
            <Box sx={{ borderBottom: `1px solid ${grey[200]}` }}>
                <RedactSelectDialog
                    open={redactSelectDialogOpen} 
                    closeDialog={() => setRedactSelectDialogOpen(false)} 
                    onAfterClose={handlePostDialogSave}
                />
                <Grid sx={{backgroundColor:  lighten(theme.palette.background.paper, 0.04)}} container>
                    <Grid item 
                        xs={6}
                        md={8}
                    >
                        <VerticalCenter>
                            <Tooltip title={display.document}>
                                <DocumentIconContainer sx={{
                                    minWidth: '60px',
                                    "&:hover": {
                                        cursor: 'pointer',
                                    }
                                }}>
                                    <VerticalCenter toCenter>
                                        <FontAwesomeIcon size='2x' color='white' icon={isInvalidFileType ? faFile : faFilePdf} />
                                    </VerticalCenter>
                                </DocumentIconContainer>
                            </Tooltip>
                            <DocumentIconDetails>
                                <VerticalCenter padding='8px'>
                                    <List>
                                        <Typography sx={{color: theme.palette.text.primary, variant:'body2'}}>{display.type} / {display.name}</Typography>
                                    </List>
                                </VerticalCenter>
                            </DocumentIconDetails>
                        </VerticalCenter>
                    </Grid>
                    <Grid item 
                        xs={6}
                        md={4}
                    >
                        <VerticalCenter toRight margin='0 15px 10px'>
                            {
                                !isInvalidFileType && 
                                <Tooltip title="Redact">
                                    <Box sx={{ marginRight: '5px' }}>
                                        <Button
                                            variant="contained"
                                            color='secondary'
                                            disabled={updating}
                                            onClick={handleRedactClick}
                                            sx={{
                                                height: '42px',
                                                minWidth: '42px'
                                            }}
                                        >
                                            <FormatPaintIcon />
                                        </Button>
                                    </Box>
                                </Tooltip>
                            }
                            <Tooltip title="Download">
                                <Box sx={{ marginRight: '15px' }}>
                                    <Button
                                        variant="contained"
                                        disabled={updating}
                                        href={`/files?fileName=${encodeURIComponent(display.document ?? '')}`}
                                        sx={{
                                            height: '42px',
                                            minWidth: '42px'
                                        }}
                                    >
                                        <DownloadIcon />
                                    </Button>
                                </Box>
                            </Tooltip>
                            <Divider orientation='vertical' />
                            <RadioGroup 
                                row
                                value={documentState}
                                onChange={(e) => handleStateClick(e.target.value)}
                                sx={{
                                    marginLeft: '12px'
                                }}
                            >
                                <Tooltip title="Verified">
                                    <StyledRadio
                                        key={`document_${document.documentId}_radio_1`}
                                        value="1"
                                        icon={<DoneIcon />}
                                        checkedIcon={<DoneIcon />}
                                        disabled={documentState === null || updating}
                                    />
                                </Tooltip>
                                <Tooltip title="Processed & Verified">
                                    <StyledRadio
                                        key={`document_${document.documentId}_radio_2`}
                                        value="2"
                                        onClick={(_e) => (documentState == "2" ? handleStateClick("2") : undefined)}
                                        icon={<GradingIcon />}
                                        checkedIcon={<GradingIcon />}
                                        disabled={documentState === null || updating}
                                    />
                                </Tooltip>
                                <Tooltip title="Pending">
                                    <StyledRadio
                                        key={`document_${document.documentId}_radio_0`}
                                        value="0"
                                        icon={<MoreHorizIcon />}
                                        checkedIcon={<MoreHorizIcon />}
                                        disabled={documentState === null || updating}
                                        staticColor='error' // <-- Custom radio button background color
                                    />
                                </Tooltip>
                            </RadioGroup>
                        </VerticalCenter>
                    </Grid>
                </Grid>
            </Box>
        </ParticipantDocumentContext.Provider>
    )
}

const Participant: FC = () => {

    const theme = useTheme();
    const client = useServiceStackClient();
    const { addAlert } = useContext(AlertContext);


    const { data: participant, loading: participantLoading, validating: participantValidating } = useContext(ParticipantContext);
    const { data: trial } = useContext(TrialContext);

    const { data: documentVerificationEvents, loading: documentVerificationEventsLoading, validating: documentVerificationEventsValidating } = useContext(DocumentVerificationEventContext);
    const { data: instituteData } = useGetTrialInstitutions(trial ? { trialId: trial.id } : undefined, { revalidateOnFocus: false });
    const { data: documentStates } = useContext(ParticipantDocumentStateContext);

    const { trialCode } = useParams();

    const [institute] = useSessionStorage<string>(`${trialCode}-institute`, 'All');
        
    const hasProcessedAndVerifiedDocuments = documentStates?.some(ds => ds.documentState === 2) ?? false;

    const filteredDocumentVerificationEvents: DocumentEvent[] | undefined = useMemo((): DocumentEvent[] | undefined => {
        if (documentVerificationEvents) {
            let copy: DocumentEvent[] | undefined = [ ...documentVerificationEvents ];
    
            copy = copy?.map(e => {
                e.forms = e.forms?.map(f => {
                    // Filter groups that do not consist of pdf files
                    f.groups = f.groups?.filter(g => !!g.files)

                    return f;
                });

                return e;
            })
    
            return copy;
        }

        else return undefined;

    }, [documentVerificationEvents]);

    const loading = participantLoading || documentVerificationEventsLoading;
    const validating = participantValidating || documentVerificationEventsValidating;

    const handleNotifyProcessedAndVerifiedDocuments = async () => {
        
        let instituteCode = instituteData?.find(institution => institution.name === institute)?.code;
        
        // Fallback: use participant's institution ID to find the code
        if (!instituteCode && participant?.participant?.institutionId) {
            instituteCode = instituteData?.find(institution => institution.id === participant?.participant?.institutionId)?.code;
        }

        if (!instituteCode) {
            addAlert({
                content: <AlertTitle>Unable to find institution code</AlertTitle>,
                timeout: 5000,
                severity: 'error',
                dismissable: true,
            });
            return;
        }

        try {
            var request = new SendNotifySitesOfProcessedAndVerifiedDocumentsEmail();
            request.patientId = parseInt(participant!.participant!.label!);
            request.institutionCode = instituteCode;
            request.trialId = trial?.secondaryIdentifier ? trial.secondaryIdentifier.toString() : undefined;
            
            // only take processed and verified documents.
            const verifiedDocumentIds = new Set<number>(
                (documentStates?.filter(ds => ds.documentState === 2).map(ds => ds.documentId).filter((id): id is number => id !== undefined)) ?? []
            );

            const formOids = filteredDocumentVerificationEvents
                ?.flatMap(e =>
                    e.forms
                        ?.filter(f =>
                            f.groups?.some(g => g.files?.some(file => verifiedDocumentIds.has(file.itemDataId!)))
                        )
                        .map(f => f.formOid)
                        .filter((id): id is string => !!id) ?? []
                )
                ?? [];

            request.formOids = formOids;
            
            var response = await client.post(request);
            //get response 
            if (response.responseStatus?.errors) {
                alert('Error sending notification email');
            }
            else{
                addAlert({
                    content: <AlertTitle>Notification email sent successfully</AlertTitle>,
                    timeout: 5000,
                    severity: 'success',
                    dismissable: true,
                }); 
            }
         
            
        } catch (error: unknown) {
            const errorMessage = error instanceof Error ? error.message : 'An error occurred';
            addAlert({
                content: <AlertTitle>{errorMessage}</AlertTitle>,
                timeout: 5000,
                severity: 'error',
                dismissable: true,
            });
        }
    };


    const { setBreadcrumbs } = useBreadcrumbs();

    useEffect(() => {
        setBreadcrumbs([
            { label: "Trials", to: "/", icon: faDna },
            { label: "Participants", to: "/"+trial?.oid, icon: faUser },
            { label: participant?.participant?.label ?? "Loading...", to: "/"+trial?.oid+ "/"+participant?.participant?.label, icon: faUser },
        ]);
    }, [participant?.participant?.label, setBreadcrumbs, trial?.oid]);
        
    return (
        <>

            <Loading name='trials' isLoading={loading} isValidating={validating} />
            <Show show={!loading}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom:'15px'  }}>
                    <Box>
                        <Typography fontWeight={300} color='secondary' variant='h2'>{trial?.uniqueIdentifier}</Typography>
                        <Typography color='secondary' variant='h3'>{participant?.participant?.label}</Typography>
                    </Box>
                    <Tooltip title="Send an email to the trial coordinators associated with this participant's site, notifying them of all document marked as 'Processed & Verified'.">
                        <span> {/* span needed to wrap disabled button for tooltip to work */}
                            <Button
                                disabled={!hasProcessedAndVerifiedDocuments}
                                variant="contained"
                                color='secondary'
                                onClick={handleNotifyProcessedAndVerifiedDocuments}
                                sx={{
                                    height: '42px',
                                    minWidth: '42px'
                                }}
                            >
                                <FontAwesomeIcon icon={faBell} style={{ color: "white" }} />
                            </Button>
                        </span>
                    </Tooltip>
                </Box>
                    {
                    filteredDocumentVerificationEvents && filteredDocumentVerificationEvents.length > 0 ? 
                    <Paper sx={{
                        padding: '15px'
                        
                    }}>
                        
                        {
                            filteredDocumentVerificationEvents.map((e, idx) => (
                                <AccordionStyled 
                                    key={`event-${idx}`} 
                                    disableGutters 
                                    defaultExpanded={true}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIconStyled />} >
                                        <Typography whiteSpace='nowrap' variant='body1' color='white'>{e?.name}</Typography>
                                        <VerticalCenter sx={{ width: '100%' }} toRight>
                                            {
                                                e.repeating && 
                                                <Box sx={{
                                                    backgroundColor: theme.palette.primary.main,
                                                    padding: '0 8px',
                                                    borderRadius: '15px',
                                                    marginRight: '15px'
                                                    
                                                }}>
                                                    <FontAwesomeIcon fontSize="0.8rem" color="#ddd" icon={faRepeat} display="inline-block" />
                                                    <Typography 
                                                        display={"inline"} 
                                                        marginLeft="5px" 
                                                        color="#ddd" 
                                                        fontSize="0.8rem" 
                                                        fontWeight='500' 
                                                        variant="body1"
                                                    >{e.repeat}</Typography>
                                                </Box>
                                            }
                                        </VerticalCenter>
                                    </AccordionSummary>
                                    <AccordionDetailsStyled>
                                        {
                                            e.forms && e.forms.map((f, i) => (
                                                <Fragment key={`form-${i}`}>
                                                    <VerticalCenter sx={{ backgroundColor: lighten(theme.palette.primary.main, 0.3), padding: '10px 15px', whiteSpace: 'normal' }}>
                                                        <Grid container>
                                                            <Grid item xs={10}>
                                                                <Typography whiteSpace='nowrap' color='white' variant='body1'>{f.formName}</Typography>
                                                            </Grid>
                                                            <Grid item xs={2}>
                                                                <VerticalCenter sx={{ width: '100%' }} toRight>
                                                                    {
                                                                        f.eventCrfId && 
                                                                        <Tooltip title={`${e.status === StudySubjectStatus.Locked ? 'View' : 'Edit'} document in ECRF`}>
                                                                            <Link
                                                                                underline='none'
                                                                                href={`${e.status === StudySubjectStatus.Locked ? `${AppSettings.ecrfUrl}/OpenClinica/ViewSectionDataEntry?ecId=${f.eventCrfId}` : `${AppSettings.ecrfUrl}/OpenClinica/AdministrativeEditing?eventCRFId=${f.eventCrfId}`}`}
                                                                                target="_blank"
                                                                            >
                                                                                <VerticalCenter sx={{
                                                                                    backgroundColor: theme.palette.secondary.main,
                                                                                    padding: '3px 8px 3px 8px',
                                                                                    borderRadius: '15px',
                                                                                    width: '125px',
                                                                                    "&:hover": {
                                                                                        backgroundColor: theme.palette.secondary.dark
                                                                                    }
                                                                                }}>
                                                                                    <FontAwesomeIcon fontSize='0.8rem' color='white' icon={e.status === StudySubjectStatus.Locked ? faLock : faLockOpen} />
                                                                                    <Typography 
                                                                                        display={"inline"} 
                                                                                        marginLeft="5px" 
                                                                                        color="#ddd" 
                                                                                        fontSize="0.8rem" 
                                                                                        fontWeight='500'
                                                                                        variant="body1"
                                                                                        whiteSpace="nowrap"
                                                                                    >{e.status === StudySubjectStatus.Locked ? 'View' : 'Edit'} ECRF Form</Typography>
                                                                                </VerticalCenter>
                                                                            </Link>
                                                                        </Tooltip>
                                                                    }
                                                                    <VerticalCenter sx={{
                                                                        backgroundColor: theme.palette.primary.main,
                                                                        padding: '0 8px 0 8px',
                                                                        borderRadius: '15px',
                                                                        marginLeft: '15px',
                                                                        whiteSpace: 'nowrap'
                                                                    }}>
                                                                        <FontAwesomeIcon fontSize="0.8rem" color="#ddd" icon={faRepeat} display="inline-block" />
                                                                        <Typography 
                                                                            display={"inline"} 
                                                                            marginLeft="5px" 
                                                                            color="#ddd" 
                                                                            fontSize="0.8rem" 
                                                                            fontWeight='500' 
                                                                            variant="body1"
                                                                        >{f?.versionName}</Typography>
                                                                    </VerticalCenter>
                                                                </VerticalCenter>
                                                            </Grid>
                                                        </Grid>
                                                    </VerticalCenter>
                                                    <List disablePadding>
                                                        {
                                                            f.groups && f.groups && f.groups.map((g, j) => (
                                                                <Fragment key={`group-${j}`}>
                                                                    <VerticalCenter sx={{ backgroundColor: lighten(theme.palette.background.paper, 0.04), color: theme.palette.text.primary, padding: '10px 15px', borderBottom: `1px solid ${grey[200]}` }}>
                                                                        <VerticalCenter minWidth='350px'>
                                                                            <Typography variant='body1'
                                                                                sx={{ color: theme.palette.text.primary }}
                                                                            >
                                                                                {
                                                                                    g.itemGroupName === "SourceDocuments" ? 
                                                                                        "Source Documents" :
                                                                                        g.itemGroupName    
                                                                                }
                                                                            </Typography>
                                                                        </VerticalCenter>
                                                                        {
                                                                            g.repeating &&
                                                                            <VerticalCenter sx={{ backgroundColor: lighten(theme.palette.background.paper, 0.04), width: '100%' }} toRight>
                                                                                <Box sx={{
                                                                                    backgroundColor: theme.palette.primary.main,
                                                                                    padding: '0 8px',
                                                                                    borderRadius: '15px'
                                                                                    
                                                                                }}>
                                                                                    <FontAwesomeIcon fontSize="0.8rem" color="#ddd" icon={faRepeat} display="inline-block" />
                                                                                    <Typography 
                                                                                        display={"inline"} 
                                                                                        marginLeft="5px" 
                                                                                        color="#ddd" 
                                                                                        fontSize="0.8rem" 
                                                                                        fontWeight='500' 
                                                                                        variant="body1"
                                                                                    >{g.repeat}</Typography>
                                                                                </Box>
                                                                            </VerticalCenter>
                                                                        }
                                                                    </VerticalCenter>
                                                                    <ParticipantFileContainer  event={e} form={f} details={g.files} />
                                                                </Fragment>
                                                            ))
                                                        }
                                                    </List>
                                                </Fragment>
                                            ))
                                        }
                                    </AccordionDetailsStyled>
                                </AccordionStyled>
                            ))
                        }
  
                    </Paper> : 
                    <Paper>
                        <List sx={{ padding: '0' }}>
                            <ListItem
                                key='no-bueno'
                                sx={{
                                    backgroundColor: "#fff",
                                    transition: '0.15s',
                                    padding: '0px',
                                    width: '100%'
                                }}
                            >
                                <VerticalCenter>
                                    <VerticalCenter sx={{
                                        backgroundColor: grey[400],
                                        height: '60px',
                                        width: '60px'
                                    }} toCenter>
                                        <FontAwesomeIcon color='white' fontSize='2.3rem' icon={faEmptySet} />
                                    </VerticalCenter>
                                    <VerticalCenter sx={{
                                        padding: '0 8px'
                                    }}>
                                        <Typography variant="h3">This participant has no available documents.</Typography>
                                    </VerticalCenter>
                                </VerticalCenter>
                            </ListItem>
                            
                        </List>
                        
                    </Paper>
                }
            </Show>
        </>
    )
}

export default Participant;
